import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Cable, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { STAFF_TESTING_DEVICE_RS232_CONFIG } from '@staff-app/graphql-client';
import {
  Button,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import {
  errorText,
  parseRs232Config,
  RS232_ENCODINGS,
  RS232_EOL_OPTIONS,
  rs232Reply,
  rs232Value,
} from '../lib/device-commands';
import { TestingDialogFrame, type TestingCommandDialogProps } from './TestingDialogFrame';
import { useDrawerScopeVars } from '../../../lib/drawer-mode';

type Mode = 'Commands' | 'TestNewCommands';

/**
 * More › Send RS232 — port of scio3 `Rs232SendDialog` (single device). Ports + saved commands come
 * from `staffTestingDeviceRs232Config` (the device team's); the send is addCommandExecution MDM_RS232
 * with `{data:[{value, encoding, eol}], type:'Commands'|'TestNewCommands', externalCom:{…port}}`. A
 * needs-response command: the device's reply is shown under "Recent".
 */
export function TestingRs232Dialog({ open, detail, commands, onClose, onDone }: TestingCommandDialogProps) {
  const scope = useDrawerScopeVars();
  const { data, loading, error } = useQuery<{ staffTestingDeviceRs232Config: string | null }>(
    STAFF_TESTING_DEVICE_RS232_CONFIG,
    { variables: { deviceId: detail._id, ...scope }, skip: !open, fetchPolicy: 'network-only' },
  );
  const { ports, commands: saved } = parseRs232Config(data?.staffTestingDeviceRs232Config);

  const [portId, setPortId] = useState('');
  const [mode, setMode] = useState<Mode>('Commands');
  const [commandId, setCommandId] = useState('');
  const [testValue, setTestValue] = useState('');
  const [encoding, setEncoding] = useState<string>('ascii');
  const [eol, setEol] = useState<string>('none');
  const [formError, setFormError] = useState('');
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState<Array<{ id: number; label: string; result: string }>>([]);

  const port = ports.find((p) => p._id === portId) ?? null;
  const savedCommand = saved.find((c) => c._id === commandId) ?? null;

  const send = async () => {
    let problem = '';
    if (!port) problem = 'Select a COM connection';
    else if (mode === 'Commands' && !savedCommand) problem = 'Select an RS232 command';
    else if (mode === 'TestNewCommands' && !testValue.trim()) problem = 'Enter a command';
    setFormError(problem);
    if (problem || !port) return;

    const value =
      mode === 'Commands' && savedCommand
        ? rs232Value(port, { type: 'Commands', command: savedCommand })
        : rs232Value(port, { type: 'TestNewCommands', value: testValue, encoding, eol });
    setSending(true);
    try {
      const reply = await commands.addCommandExecution('MDM_RS232', value);
      toast.success('Sent successfully');
      const label = mode === 'Commands' ? (savedCommand?.name ?? 'Command') : testValue.trim();
      setRecent((prev) =>
        [{ id: prev.length + 1, label, result: rs232Reply(reply) ?? 'No reply' }, ...prev].slice(0, 10),
      );
      onDone();
    } catch (err) {
      toast.error(errorText(err, 'Failed to send RS232 command'));
    } finally {
      setSending(false);
    }
  };

  return (
    <TestingDialogFrame
      open={open}
      onClose={onClose}
      title={'Send RS232'}
      icon={Cable}
      testId="testing-rs232-dialog"
      size="lg"
      closeLabel={'Close'}
      footer={
        <Button onClick={() => void send()} disabled={sending || !ports.length} data-testid="testing-rs232-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {'Send'}
        </Button>
      }
    >
      {loading && !data ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{`Couldn't load COM connections: ${error.message}`}</p>
      ) : !ports.length ? (
        <p className="text-sm text-muted-foreground" data-testid="testing-rs232-no-ports">
          {"This device's team has no COM connections. Add one in the customer portal first."}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            <Label htmlFor="testing-rs232-port">{'COM Connection'}</Label>
            <Select value={portId || undefined} onValueChange={setPortId}>
              <SelectTrigger id="testing-rs232-port" className="cursor-pointer">
                <SelectValue placeholder={'Select a COM connection…'} />
              </SelectTrigger>
              <SelectContent>
                {ports.map((p) => (
                  <SelectItem key={p._id} value={p._id}>
                    {`${p.name ?? p._id}${p.path ? ` (${p.path})` : ''}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <RadioGroup
            value={mode}
            onValueChange={(v) => setMode(v === 'TestNewCommands' ? 'TestNewCommands' : 'Commands')}
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="Commands" id="testing-rs232-mode-saved" aria-label={'Saved command'} />
              <Label htmlFor="testing-rs232-mode-saved" className="cursor-pointer text-sm font-normal">
                {'Saved command'}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="TestNewCommands" id="testing-rs232-mode-test" aria-label={'Test a new command'} />
              <Label htmlFor="testing-rs232-mode-test" className="cursor-pointer text-sm font-normal">
                {'Test a new command'}
              </Label>
            </div>
          </RadioGroup>

          {mode === 'Commands' ? (
            <div className="space-y-2">
              <Label htmlFor="testing-rs232-command">{'RS232 Command'}</Label>
              <Select value={commandId || undefined} onValueChange={setCommandId}>
                <SelectTrigger id="testing-rs232-command" className="cursor-pointer">
                  <SelectValue placeholder={saved.length ? 'Select a command…' : 'No saved commands'} />
                </SelectTrigger>
                <SelectContent>
                  {saved.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.name ?? c.value ?? c._id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="testing-rs232-value">{'Command'}</Label>
                <Input
                  id="testing-rs232-value"
                  value={testValue}
                  onChange={(e) => setTestValue(e.target.value)}
                  className="font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="testing-rs232-encoding">{'Encoding'}</Label>
                  <Select value={encoding} onValueChange={setEncoding}>
                    <SelectTrigger id="testing-rs232-encoding" className="cursor-pointer">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RS232_ENCODINGS.map((e) => (
                        <SelectItem key={e} value={e}>
                          {e.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="testing-rs232-eol">{'End of line'}</Label>
                  <Select value={eol} onValueChange={setEol}>
                    <SelectTrigger id="testing-rs232-eol" className="cursor-pointer">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RS232_EOL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {formError ? (
        <p className="text-xs text-destructive" data-testid="testing-rs232-error">
          {formError}
        </p>
      ) : null}

      {recent.length ? (
        <div className="space-y-2" data-testid="testing-rs232-recent">
          <div className="text-xs font-semibold text-muted-foreground">{'Recent'}</div>
          <ul className="divide-y rounded-md border text-xs">
            {recent.map((r) => (
              <li key={r.id} className="flex gap-3 px-3 py-2">
                <span className="shrink-0 font-mono font-medium">{r.label}</span>
                <span className="min-w-0 break-all font-mono text-muted-foreground">{r.result}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </TestingDialogFrame>
  );
}
